import React from 'react';
import { 
  Zap, 
  Layers, 
  BarChart3, 
  Globe, 
  ShieldCheck
} from 'lucide-react';
import './ScalableElastic.css';
import Hero from '../../components/Hero/Hero';
import FAQ from '../../components/FAQ/FAQ';
import CTASection from '../../components/CTA/CTA';
import Footer from '../../components/Footer/Footer';

const services = [
  {
    title: 'Auto-Scaling Groups',
    description: 'Automatically adjust capacity to maintain steady, predictable performance at the lowest possible cost.',
    features: [
      'Scale up/down based on demand',
      'Maintain application availability',
      'Dynamic & predictive scaling'
    ],
    image: '/images/scalable-elastic/auto-scaling.jpg'
  },
  {
    title: 'Load Balancing',
    description: 'Distribute incoming application traffic across multiple targets to ensure fault tolerance.',
    features: [
      'Layer 7 & Layer 4 balancing',
      'Health checks & automatic routing',
      'SSL/TLS termination'
    ],
    image: '/images/scalable-elastic/load-balancing.jpg'
  },
  {
    title: 'Serverless Infrastructure',
    description: 'Run code without thinking about servers. Only pay for the compute time you consume.',
    features: [
      'Event-driven execution',
      'Zero management overhead',
      'Millisecond-level scaling'
    ],
    image: '/images/scalable-elastic/serverless.jpg'
  }
];

const capabilities = [
  {
    icon: <Zap size={24} />,
    title: 'High Velocity',
    text: 'Deploy updates instantly with zero-downtime rolling deployments and blue-green strategies.'
  },
  {
    icon: <Layers size={24} />,
    title: 'Microservices',
    text: 'Decouple your monolith into scalable, independent services that can be managed and scaled individually.'
  },
  {
    icon: <BarChart3 size={24} />,
    title: 'Elastic Storage',
    text: 'Scale your data storage from gigabytes to petabytes without ever hitting a ceiling.'
  },
  {
    icon: <Globe size={24} />,
    title: 'Multi-Region',
    text: 'Serve global users with low latency by deploying your infrastructure across multiple worldwide zones.'
  }
];

const faqItems = [
  {
    question: 'What is the difference between Scalable and Elastic?',
    answer: 'Scalability is the ability of a system to handle increased load by adding resources. Elasticity is the ability of a system to automatically add or remove resources in real-time based on current demand.'
  },
  {
    question: 'How do you handle database scaling?',
    answer: 'We implement read-replicas, sharding, and managed relational or NoSQL databases that support horizontal scaling to ensure your data layer never becomes a bottleneck.'
  },
  {
    question: 'Will auto-scaling increase my costs?',
    answer: 'Actually, it often decreases costs. By scaling down during low-traffic periods (like nights or weekends), you stop paying for idle capacity you are not using.'
  }
];

export default function ScalableElastic() {
  return (
    <article className="scalable-page">
      <Hero 
        eyebrow="Scalable & Elastic"
        title={
          <>
            Infinite <span className="hero-accent">Growth</span>
            <br /> & Stability.
          </>
        }
        description="Build infrastructure that grows with your business. We design elastic systems that handle peak traffic without breaking a sweat and scale down to save costs when idle."
        primaryAction={{ label: "Start Scaling", link: "#services" }}
        secondaryAction={{ label: "Technical Docs", link: "#faq" }}
        stats={[
          { label: "High Availability", value: "99.99% Guaranteed uptime" },
          { label: "Resource Efficiency", value: "Up to 40% cost reduction" }
        ]}
      />

      <section className="section section-capabilities" id="services">
        <div className="section-heading">
          <span className="eyebrow">Core Capabilities</span>
          <h2>Infrastructure designed to adapt to your demands.</h2>
          <p>We leverage modern cloud-native patterns to ensure your application remains responsive regardless of user volume.</p>
        </div>
        <div className="capabilities-grid">
          {capabilities.map((cap, i) => (
            <div key={i} className="capability-card">
              <div className="cap-icon">{cap.icon}</div>
              <h3>{cap.title}</h3>
              <p>{cap.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section section-solutions">
        <div className="solutions-container">
          {services.map((service, index) => (
            <div key={index} className={`solution-row ${index % 2 !== 0 ? 'reverse' : ''}`}>
              <div className="solution-copy">
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <ul className="feature-list">
                  {service.features.map((f, fi) => (
                    <li key={fi}><ShieldCheck size={16} /> {f}</li>
                  ))}
                </ul>
              </div>
              <div className="solution-media">
                <img src={service.image} alt={service.title} />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="section section-metrics">
        <div className="metrics-grid">
          <div className="metric-item">
            <strong>99.99%</strong>
            <span>Uptime during peaks</span>
          </div>
          <div className="metric-item">
            <strong>&lt; 200ms</strong>
            <span>Global latency</span>
          </div>
          <div className="metric-item">
            <strong>40%</strong>
            <span>Avg. cost reduction</span>
          </div>
          <div className="metric-item">
            <strong>Instant</strong>
            <span>Burst capacity</span>
          </div>
        </div>
      </section>

      <section className="section section-faq">
        <div className="section-heading">
          <span className="eyebrow">Deep Dive</span>
          <h2>Scalability Questions.</h2>
        </div>
        <FAQ items={faqItems} />
      </section>

      <CTASection />
      
      <Footer />
    </article>
  );
}
